import { driver, type Config, type Driver } from 'driver.js'
import { endPoint } from '@/constant/end-point'
import { normalizeClientRoutePath, UserGuideTrigger } from '@/constant/user-guide'
import { useActiveOrg } from '@/hooks/use-active-org'
import useAPI from '@/hooks/use-api'
import { useUserPreference } from '@/hooks/use-user-preference'
import type { IUserGuide } from '@/interface/user-guide.interface'
import { isOrgRole } from '@/utils/role-check.utils'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

const waitForSelector = async (selector: string, timeoutMs = 4000) => {
  const started = Date.now()
  while (Date.now() - started < timeoutMs) {
    try {
      if (document.querySelector(selector)) return true
    } catch {
      return false
    }
    await new Promise((resolve) => setTimeout(resolve, 120))
  }
  return false
}

export function useUserGuidePlayer() {
  const location = useLocation()
  const { activeOrg } = useActiveOrg()
  const { userPreferences } = useUserPreference()
  const { getOne, post } = useAPI<IUserGuide>()
  const driverRef = useRef<Driver | null>(null)
  const destroyedByNavigationRef = useRef(false)
  const routePath = normalizeClientRoutePath(location.pathname)
  const isHelpTourDisabled = userPreferences?.find((preference) => preference.key === 'help-tour')?.value === 'off'
  const isAdminPath = routePath.startsWith('/admin')
  const isOrgUser = Boolean(activeOrg?.role && isOrgRole(activeOrg.role as string))
  const enabled =
    !isAdminPath &&
    isOrgUser &&
    Boolean(activeOrg?.isOnboardingCompleted) &&
    !isHelpTourDisabled &&
    Boolean(activeOrg?.id)

  const { data: guide } = useQuery({
    queryKey: [endPoint.userGuides, 'current', routePath, activeOrg?.id],
    queryFn: async () => {
      const response = await getOne(`${endPoint.userGuides}/current`, { routePath })
      if (!response.status) throw new Error(response.message)
      return response.data
    },
    enabled,
  })

  const saveProgress = useCallback(
    async (completed: boolean, dismissed: boolean) => {
      if (!guide?.id) return
      await post(`${endPoint.userGuides}/${guide.id}/progress`, {
        completed,
        dismissed,
        lastStepIndex: 0,
      } as Partial<IUserGuide>)
    },
    [guide?.id, post]
  )

  const startTour = useCallback(async () => {
    if (!guide?.steps?.length) return
    await waitForSelector(guide.steps[0].selector)

    const steps: Config['steps'] = guide.steps
      .filter((step) => {
        try {
          return Boolean(document.querySelector(step.selector))
        } catch {
          return false
        }
      })
      .map((step) => ({
        element: step.selector,
        popover: {
          title: step.title,
          description: step.description,
          side: (step.side as 'top' | 'bottom' | 'left' | 'right' | undefined) ?? undefined,
          align: (step.align as 'start' | 'center' | 'end' | undefined) ?? undefined,
        },
      }))

    if (!steps.length) return

    destroyedByNavigationRef.current = false
    driverRef.current?.destroy()
    const instance = driver({
      steps,
      showProgress: true,
      overlayOpacity: 0.5,
      onDestroyed: () => {
        if (destroyedByNavigationRef.current) return
        void saveProgress(true, false)
      },
    })
    driverRef.current = instance
    instance.drive()
  }, [guide, saveProgress])

  useEffect(() => {
    if (!enabled || !guide) return
    const shouldAutoStart =
      (guide.trigger === UserGuideTrigger.FIRST_VISIT || guide.trigger === UserGuideTrigger.FIRST_VISIT_AND_MANUAL) &&
      !guide.progress?.completedAt &&
      !guide.progress?.dismissedAt

    if (!shouldAutoStart) return
    const timeout = window.setTimeout(() => {
      void startTour()
    }, 400)
    return () => window.clearTimeout(timeout)
  }, [enabled, guide, startTour])

  useEffect(() => {
    return () => {
      destroyedByNavigationRef.current = true
      driverRef.current?.destroy()
    }
  }, [routePath])

  const canShowButton =
    enabled &&
    Boolean(guide) &&
    (guide?.trigger === UserGuideTrigger.MANUAL || guide?.trigger === UserGuideTrigger.FIRST_VISIT_AND_MANUAL)

  return { startTour, canShowButton, guide }
}
